import React from 'react';
import { Pencil, Trash2, BookOpen, Eye, EyeOff, Loader2 } from 'lucide-react';
import type { ResourceSeries } from '../../../hooks/useAdminSeries';

interface Props {
  series: ResourceSeries[];
  onEdit: (s: ResourceSeries) => void;
  onManageSermons: (s: ResourceSeries) => void;
  onTogglePublished: (id: string, published: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function SeriesManagerList({ series, onEdit, onManageSermons, onTogglePublished, onDelete }: Props) {
  const [togglingId, setTogglingId] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const handleToggle = async (s: ResourceSeries) => {
    setTogglingId(s.id);
    try { await onTogglePublished(s.id, !s.published); } finally { setTogglingId(null); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this series and all its sermons and assets? This cannot be undone.')) return;
    setDeletingId(id);
    try { await onDelete(id); } finally { setDeletingId(null); }
  };

  if (series.length === 0) {
    return (
      <p className="text-sm text-slate dark:text-soft-white/50 py-8 text-center">
        No series yet. Create your first one above.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-sand/20 dark:border-slate/20 text-left text-xs text-slate dark:text-soft-white/50 uppercase tracking-wide">
            <th className="pb-2 pr-4 font-medium">Title</th>
            <th className="pb-2 pr-4 font-medium">Book / Topic</th>
            <th className="pb-2 pr-4 font-medium text-center">Weeks</th>
            <th className="pb-2 pr-4 font-medium text-center">Status</th>
            <th className="pb-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sand/10 dark:divide-slate/10">
          {series.map(s => (
            <tr key={s.id} className="group">
              <td className="py-3 pr-4">
                <div className="flex items-center gap-3">
                  {s.cover_art_url
                    ? <img src={s.cover_art_url} alt="" className="h-8 w-14 rounded object-cover shrink-0" />
                    : <div className="h-8 w-14 rounded bg-sand/30 dark:bg-slate/20 shrink-0" />
                  }
                  <span className="font-medium text-midnight dark:text-soft-white truncate max-w-[200px]">{s.title}</span>
                </div>
              </td>
              <td className="py-3 pr-4 text-slate dark:text-soft-white/60">
                {s.book_of_bible ?? s.topic ?? '—'}
              </td>
              <td className="py-3 pr-4 text-center text-slate dark:text-soft-white/60">{s.week_count}</td>
              <td className="py-3 pr-4 text-center">
                <button
                  onClick={() => handleToggle(s)}
                  disabled={togglingId === s.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors"
                  style={{ minWidth: 80 }}
                >
                  {togglingId === s.id
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : s.published
                    ? <><Eye className="h-3 w-3 text-emerald" /><span className="text-emerald">Published</span></>
                    : <><EyeOff className="h-3 w-3 text-slate" /><span className="text-slate dark:text-soft-white/50">Draft</span></>
                  }
                </button>
              </td>
              <td className="py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => onManageSermons(s)}
                    className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate dark:text-soft-white/60"
                    title="Manage sermons"
                  >
                    <BookOpen className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit(s)}
                    className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate dark:text-soft-white/60"
                    title="Edit series"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    disabled={deletingId === s.id}
                    className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400"
                    title="Delete series"
                  >
                    {deletingId === s.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Trash2 className="h-4 w-4" />
                    }
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
